import React from 'react'
import { isNull } from 'lodash'
import {
  SortDirectionEnum,
  TestsFull_tests_pagination,
  TestsResultsAnalytics_testsResultsAnalytics_tests,
  getFormattedDateAndTime,
  TestSessionPaymentStatus,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Label from 'components/Label'
import Card from 'components/Card'
import { Box } from 'components/Layout'

interface ColumnLabels {
  patient?: string | null
  barcode?: string | null
  testCategory?: string | null
  result?: string | null
  appointmentDate?: string | null
  testedAt?: string | null
  resultProvisionedAt?: string | null
  labName?: string | null
  paymentStatus?: string | null
  paymentMethod?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  tests: TestsResultsAnalytics_testsResultsAnalytics_tests[]
  pagination?: TestsFull_tests_pagination
  sorter?: DataListProps['sorter']
  requestDebounceInMs?: number
  isLoading: boolean
  pageLength?: number
  columnLabels?: ColumnLabels
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onPageChange?: (page: number) => void
  onRowClick?: (data: TestsResultsAnalytics_testsResultsAnalytics_tests) => void
}

const defaultColumnLabels = {
  patient: 'Patient',
  insuranceCompany: 'Insurance Company',
  insuranceStatus: 'Insurance Status',
  barcode: 'Barcode',
  testCategory: 'Test category',
  testedAt: 'Test date',
  resultProvisionedAt: 'Result date',
  labName: 'Lab name',
}

const PAGE_LENGTH = 10

export const InsuranceAnalyticsTestsResultsDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  tests,
  sorter,
  pagination,
  isLoading,
  columnLabels: providedColumnLabels,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])

  return (
    <>
      <Card>
        <Datalist
          header={tableHeader}
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={tests}
          sorter={sorter}
          onSortChange={onSortChange}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={() => (
            <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
              {!isNull(columnLabels.patient) ? (
                <Datalist.HeaderCell label={columnLabels.patient} />
              ) : null}
              {!isNull(columnLabels.insuranceCompany) ? (
                <Datalist.HeaderCell label={columnLabels.insuranceCompany} />
              ) : null}
              {!isNull(columnLabels.insuranceStatus) ? (
                <Datalist.HeaderCell label={columnLabels.insuranceStatus} />
              ) : null}
              {!isNull(columnLabels.barcode) ? (
                <Datalist.HeaderCell label={columnLabels.barcode} />
              ) : null}
              {!isNull(columnLabels.testCategory) ? (
                <Datalist.HeaderCell label={columnLabels.testCategory} />
              ) : null}
              {!isNull(columnLabels.testedAt) ? (
                <Datalist.HeaderCell label={columnLabels.testedAt} />
              ) : null}
              {!isNull(columnLabels.resultProvisionedAt) ? (
                <Datalist.HeaderCell label={columnLabels.resultProvisionedAt} />
              ) : null}
              {!isNull(columnLabels.labName) ? (
                <Datalist.HeaderCell label={columnLabels.labName} />
              ) : null}
            </Datalist.Row>
          )}
          renderTableRow={(data) => {
            const {
              barcode,
              Insurance,
              resultProvisionedAt,
              TestSession,
              User,
              testedAt,
              insuranceStatus,
            } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                onClick={
                  onRowClick
                    ? () => {
                        onRowClick(data)
                      }
                    : undefined
                }
                {...(!isDesktop ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.patient) ? (
                  <Datalist.Cell label={columnLabels.patient}>
                    {User && (
                      <>
                        {User.firstName} {User.lastName} ({getFormattedDateAndTime(User.birthDate)})
                      </>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.insuranceCompany) ? (
                  <Datalist.Cell label={columnLabels.insuranceCompany}>
                    {Insurance &&
                    Insurance.insuranceCompany &&
                    TestSession.paymentStatus == TestSessionPaymentStatus.Insurance
                      ? Insurance.insuranceCompany
                      : ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.insuranceStatus) ? (
                  <Datalist.Cell label={columnLabels.insuranceStatus}>
                    {insuranceStatus}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.barcode) ? (
                  <Datalist.Cell label={columnLabels.barcode}>
                    <Box>{barcode}</Box>
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testCategory) ? (
                  <Datalist.Cell label={columnLabels.testCategory}>
                    {TestSession.type}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.testedAt) ? (
                  <Datalist.Cell label={columnLabels.testedAt}>
                    {getFormattedDateAndTime(testedAt, true)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.resultProvisionedAt) ? (
                  <Datalist.Cell label={columnLabels.resultProvisionedAt}>
                    {resultProvisionedAt ? (
                      getFormattedDateAndTime(resultProvisionedAt, true)
                    ) : (
                      <Label indicator="red">Not provisionet yet</Label>
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.labName) ? (
                  <Datalist.Cell label={columnLabels.labName}>{TestSession.lab}</Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
          footer={tableFooter}
        />
      </Card>
      {pagination && pagination.total > pagination.length && onPageChange && (
        <Pagination.Wrapper>
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length) || 1}
            currentPage={Math.ceil(pagination.from / pagination.length) + 1}
            onPageChange={onPageChange}
          />
        </Pagination.Wrapper>
      )}
    </>
  )
}
